// In-app "Guide" page — a visual explainer of the UniPortal data model and
// roles for internal staff. Purely presentational (no API / no state): it
// renders the hierarchy (Publishers → Collections → Documents), the m:n
// relationships, the access predicate, and the three roles as diagrams built
// from flex/grid + icon chips + connecting lines. All copy lives in the
// `guide` i18n namespace. Rendered inside <AppShell> like every other page.
import { useTranslation } from 'react-i18next';
import {
  Building2,
  FolderTree,
  FileCode2,
  Inbox,
  Users,
  Link2,
  Copy,
  Eye,
  UploadCloud,
  ShieldCheck,
  Sparkles,
  ArrowRight,
  CornerDownRight,
} from 'lucide-react';
import { AppShell } from '@/components/AppShell';
import { useAuth } from '@/hooks/useAuth';
import { cn } from '@/lib/utils';

export function Guide() {
  const { t } = useTranslation('guide');
  const { canPublish, isPlatformAdmin } = useAuth();

  // Regular viewers get a minimized guide — only what's relevant to a reader.
  // The full data-model guide is for publishers + platform admins.
  if (!canPublish && !isPlatformAdmin) return <GuideBasic />;

  return (
    <AppShell>
      {/* ── Hero ──────────────────────────────────────────────── */}
      <section className="mb-14 animate-fade-up">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-purple/70 mb-3 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" />
          {t('hero.eyebrow')}
        </p>
        <h1 className="text-4xl sm:text-5xl font-medium tracking-tight gradient-text pb-2 max-w-3xl">
          {t('hero.title')}
        </h1>
        <p className="text-base font-light text-slate-500 mt-2 max-w-2xl leading-relaxed">
          {t('hero.intro')}
        </p>

        {/* Flow ribbon: Publish → Organise → Grant → Read */}
        <div className="mt-8 flex flex-wrap items-stretch gap-3">
          {(
            [
              { icon: UploadCloud, key: 'publish' },
              { icon: FolderTree, key: 'organise' },
              { icon: ShieldCheck, key: 'grant' },
              { icon: Eye, key: 'read' },
            ] as const
          ).map((step, i, arr) => (
            <div key={step.key} className="flex items-stretch gap-3">
              <div className="flex items-center gap-3 rounded-2xl bg-white border border-slate-200/80 shadow-card px-4 py-3">
                <div className="p-[2px] rounded-xl bg-brand-gradient shrink-0">
                  <div className="p-2 rounded-xl bg-white">
                    <step.icon className="w-4 h-4 text-brand-purple" />
                  </div>
                </div>
                <div className="leading-tight">
                  <p className="text-[13px] font-semibold text-ink">{t(`hero.flow.${step.key}.title`)}</p>
                  <p className="text-[11px] font-light text-slate-400">{t(`hero.flow.${step.key}.body`)}</p>
                </div>
              </div>
              {i < arr.length - 1 && (
                <div className="hidden sm:flex items-center text-slate-300">
                  <ArrowRight className="w-4 h-4" />
                </div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* ── 1. The hierarchy ──────────────────────────────────── */}
      <Section index="01" title={t('hierarchy.title')} subtitle={t('hierarchy.subtitle')} delay={0.04}>
        <div className="rounded-2xl bg-white border border-slate-200/80 shadow-card p-6 sm:p-7">
          {/* Publisher card */}
          <div className="rounded-2xl border-2 border-brand-purple/15 bg-brand-purple/[0.02] p-5">
            <div className="flex items-center gap-3 mb-5">
              <div className="p-[2px] rounded-xl bg-brand-gradient shrink-0">
                <div className="p-2 rounded-xl bg-white">
                  <Building2 className="w-5 h-5 text-brand-purple" />
                </div>
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-brand-purple/60">
                  {t('hierarchy.publisherTag')}
                </p>
                <p className="text-sm font-semibold text-ink">{t('hierarchy.publisherExample')}</p>
              </div>
            </div>

            {/* Collections row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {(['circet', 'ctl', 'nonCollection'] as const).map((col) => {
                const isSystem = col === 'nonCollection';
                return (
                  <div
                    key={col}
                    className={cn(
                      'rounded-xl border bg-white p-4',
                      isSystem ? 'border-dashed border-slate-300' : 'border-slate-200',
                    )}
                  >
                    <div className="flex items-center gap-2 mb-3">
                      {isSystem ? (
                        <Inbox className="w-4 h-4 text-slate-400 shrink-0" />
                      ) : (
                        <FolderTree className="w-4 h-4 text-brand-rose shrink-0" />
                      )}
                      <p
                        className={cn(
                          'text-[13px] font-semibold truncate',
                          isSystem ? 'text-slate-500' : 'text-ink',
                        )}
                      >
                        {t(`hierarchy.collections.${col}`)}
                      </p>
                    </div>
                    {/* Document chips */}
                    <div className="space-y-1.5">
                      {[0, 1].map((d) => (
                        <div
                          key={d}
                          className="flex items-center gap-2 rounded-lg border border-slate-100 bg-slate-50/60 px-2.5 py-1.5"
                        >
                          <FileCode2 className="w-3.5 h-3.5 text-brand-purple/50 shrink-0" />
                          <span className="text-[11px] font-medium text-slate-500 truncate">
                            {t(`hierarchy.doc.${col}.${d}`)}
                          </span>
                        </div>
                      ))}
                    </div>
                    {isSystem && (
                      <p className="text-[10px] font-light text-slate-400 mt-3 leading-snug">
                        {t('hierarchy.nonCollectionNote')}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Legend */}
          <div className="mt-5 flex flex-wrap gap-x-6 gap-y-2">
            {(
              [
                { icon: Building2, key: 'publisher', cls: 'text-brand-purple' },
                { icon: FolderTree, key: 'collection', cls: 'text-brand-rose' },
                { icon: FileCode2, key: 'document', cls: 'text-brand-orange' },
              ] as const
            ).map((l) => (
              <div key={l.key} className="flex items-center gap-2">
                <l.icon className={cn('w-4 h-4', l.cls)} />
                <span className="text-xs text-slate-500">
                  <span className="font-semibold text-ink">{t(`hierarchy.legend.${l.key}.term`)}</span>
                  {' — '}
                  {t(`hierarchy.legend.${l.key}.def`)}
                </span>
              </div>
            ))}
          </div>
        </div>
      </Section>

      {/* ── 2. Many-to-many ───────────────────────────────────── */}
      <Section index="02" title={t('m2m.title')} subtitle={t('m2m.subtitle')} delay={0.08}>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* 2a — one doc, many collections */}
          <div className="rounded-2xl bg-white border border-slate-200/80 shadow-card p-6">
            <div className="flex items-center gap-2 mb-1">
              <Link2 className="w-4 h-4 text-brand-rose" />
              <h3 className="text-sm font-semibold text-ink">{t('m2m.docCollections.title')}</h3>
            </div>
            <p className="text-xs font-light text-slate-500 mb-5 leading-relaxed">
              {t('m2m.docCollections.body')}
            </p>

            {/* Diagram: 1 document → 2 collections */}
            <div className="flex items-center justify-between gap-3">
              <div className="flex items-center gap-2 rounded-xl border border-brand-orange/30 bg-brand-orange/[0.04] px-3 py-2.5">
                <FileCode2 className="w-4 h-4 text-brand-orange shrink-0" />
                <span className="text-[12px] font-semibold text-ink">{t('m2m.docCollections.doc')}</span>
              </div>
              <div className="flex-1 flex flex-col items-center text-slate-300">
                <span className="text-[10px] font-bold text-brand-rose mb-0.5">{t('m2m.inBadge')}</span>
                <div className="w-full h-px bg-gradient-to-r from-brand-orange/40 to-brand-rose/40" />
              </div>
              <div className="space-y-2">
                {['circet', 'shared'].map((c) => (
                  <div
                    key={c}
                    className="flex items-center gap-2 rounded-xl border border-brand-rose/30 bg-brand-rose/[0.03] px-3 py-2"
                  >
                    <FolderTree className="w-3.5 h-3.5 text-brand-rose shrink-0" />
                    <span className="text-[11px] font-medium text-ink">
                      {t(`m2m.docCollections.col.${c}`)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* 2b — one doc, many publishers (dedupe / link) */}
          <div className="rounded-2xl bg-white border border-slate-200/80 shadow-card p-6">
            <div className="flex items-center gap-2 mb-1">
              <Copy className="w-4 h-4 text-brand-purple" />
              <h3 className="text-sm font-semibold text-ink">{t('m2m.docPublishers.title')}</h3>
            </div>
            <p className="text-xs font-light text-slate-500 mb-5 leading-relaxed">
              {t('m2m.docPublishers.body')}
            </p>

            {/* Diagram: 2 publishers → 1 shared document */}
            <div className="flex items-center gap-3">
              <div className="space-y-2">
                <div className="flex items-center gap-2 rounded-xl border border-brand-purple/25 bg-brand-purple/[0.03] px-3 py-2">
                  <Building2 className="w-3.5 h-3.5 text-brand-purple shrink-0" />
                  <span className="text-[11px] font-medium text-ink">{t('m2m.docPublishers.deptA')}</span>
                  <span className="text-[9px] font-bold uppercase tracking-wide text-emerald-600 bg-emerald-50 rounded px-1 py-0.5">
                    {t('m2m.docPublishers.primary')}
                  </span>
                </div>
                <div className="flex items-center gap-2 rounded-xl border border-brand-purple/25 bg-brand-purple/[0.03] px-3 py-2">
                  <Building2 className="w-3.5 h-3.5 text-brand-purple shrink-0" />
                  <span className="text-[11px] font-medium text-ink">{t('m2m.docPublishers.deptB')}</span>
                  <span className="text-[9px] font-bold uppercase tracking-wide text-slate-500 bg-slate-100 rounded px-1 py-0.5">
                    {t('m2m.docPublishers.secondary')}
                  </span>
                </div>
              </div>
              <div className="flex-1 flex flex-col items-center text-slate-300">
                <span className="text-[10px] font-bold text-brand-purple mb-0.5">{t('m2m.linkBadge')}</span>
                <div className="w-full h-px bg-gradient-to-r from-brand-purple/40 to-brand-orange/40" />
              </div>
              <div className="flex flex-col items-center gap-1 rounded-xl border border-brand-orange/30 bg-brand-orange/[0.04] px-3 py-2.5">
                <FileCode2 className="w-4 h-4 text-brand-orange" />
                <span className="text-[11px] font-semibold text-ink text-center leading-tight">
                  {t('m2m.docPublishers.oneDoc')}
                </span>
              </div>
            </div>
            <p className="text-[11px] font-light text-slate-400 mt-4 leading-snug flex items-start gap-1.5">
              <CornerDownRight className="w-3.5 h-3.5 mt-0.5 shrink-0 text-slate-300" />
              {t('m2m.docPublishers.note')}
            </p>
          </div>
        </div>
      </Section>

      {/* ── 3. Access predicate ───────────────────────────────── */}
      <Section index="03" title={t('access.title')} subtitle={t('access.subtitle')} delay={0.12}>
        <div className="rounded-2xl bg-white border border-slate-200/80 shadow-card p-6 sm:p-8">
          <div className="flex flex-col lg:flex-row items-stretch gap-4">
            {/* Path A — collection grant */}
            <div className="flex-1 rounded-xl border border-brand-purple/20 bg-brand-purple/[0.02] p-5">
              <div className="flex items-center gap-2 mb-3">
                <FolderTree className="w-4 h-4 text-brand-purple" />
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-brand-purple">
                  {t('access.pathA.tag')}
                </span>
              </div>
              <p className="text-sm font-semibold text-ink mb-1">{t('access.pathA.title')}</p>
              <p className="text-xs font-light text-slate-500 leading-relaxed">{t('access.pathA.body')}</p>
            </div>

            {/* OR pill */}
            <div className="flex lg:flex-col items-center justify-center gap-2">
              <div className="h-px lg:h-full lg:w-px flex-1 bg-slate-200" />
              <span className="shrink-0 inline-flex items-center justify-center rounded-full bg-brand-gradient text-white text-xs font-bold w-11 h-11 shadow-card">
                {t('access.or')}
              </span>
              <div className="h-px lg:h-full lg:w-px flex-1 bg-slate-200" />
            </div>

            {/* Path B — document grant */}
            <div className="flex-1 rounded-xl border border-brand-rose/20 bg-brand-rose/[0.02] p-5">
              <div className="flex items-center gap-2 mb-3">
                <FileCode2 className="w-4 h-4 text-brand-rose" />
                <span className="text-[10px] font-bold uppercase tracking-[0.14em] text-brand-rose">
                  {t('access.pathB.tag')}
                </span>
              </div>
              <p className="text-sm font-semibold text-ink mb-1">{t('access.pathB.title')}</p>
              <p className="text-xs font-light text-slate-500 leading-relaxed">{t('access.pathB.body')}</p>
            </div>
          </div>

          {/* Result row */}
          <div className="mt-5 flex items-center gap-3 rounded-xl border border-emerald-200 bg-emerald-50/60 px-5 py-4">
            <div className="p-[2px] rounded-lg bg-brand-gradient shrink-0">
              <div className="p-1.5 rounded-lg bg-white">
                <Eye className="w-4 h-4 text-emerald-600" />
              </div>
            </div>
            <div>
              <p className="text-sm font-semibold text-ink">{t('access.result.title')}</p>
              <p className="text-xs font-light text-slate-500 mt-0.5">{t('access.result.body')}</p>
            </div>
          </div>

          {/* Predicate formula */}
          <div className="mt-4 flex flex-wrap items-center gap-2 text-xs">
            <span className="font-mono font-semibold text-brand-purple bg-brand-purple/5 rounded px-2 py-1">
              {t('access.formula.visible')}
            </span>
            <span className="text-slate-400 font-mono">=</span>
            <span className="font-mono font-medium text-brand-rose bg-brand-rose/5 rounded px-2 py-1">
              {t('access.formula.docGrant')}
            </span>
            <span className="text-slate-400 font-mono font-bold">{t('access.formula.or')}</span>
            <span className="font-mono font-medium text-brand-purple bg-brand-purple/5 rounded px-2 py-1">
              {t('access.formula.colGrant')}
            </span>
          </div>
        </div>
      </Section>

      {/* ── 4. Roles ──────────────────────────────────────────── */}
      <Section index="04" title={t('roles.title')} subtitle={t('roles.subtitle')} delay={0.16}>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {(
            [
              { key: 'viewer', icon: Eye, accent: 'orange' },
              { key: 'publisher', icon: UploadCloud, accent: 'rose' },
              { key: 'admin', icon: ShieldCheck, accent: 'purple' },
            ] as const
          ).map((role) => (
            <RoleCard key={role.key} roleKey={role.key} icon={role.icon} accent={role.accent} t={t} />
          ))}
        </div>
      </Section>
    </AppShell>
  );
}

// ── Section wrapper — editorial header + staggered reveal ──────

function Section({
  index,
  title,
  subtitle,
  delay,
  children,
}: {
  index: string;
  title: string;
  subtitle: string;
  delay: number;
  children: React.ReactNode;
}) {
  return (
    <section className="mb-14 animate-fade-up" style={{ animationDelay: `${delay}s` }}>
      <div className="flex items-center gap-4 mb-5">
        <span className="text-xs font-bold tabular-nums text-brand-purple/40 shrink-0">{index}</span>
        <div className="shrink-0">
          <h2 className="text-xl font-medium tracking-tight text-ink">{title}</h2>
          <p className="text-sm font-light text-slate-500">{subtitle}</p>
        </div>
        <div className="gradient-rule flex-1 self-end mb-2" />
      </div>
      {children}
    </section>
  );
}

// ── Role card — header chip, capability checklist, scope limit ──

const ACCENT: Record<'orange' | 'rose' | 'purple', { ring: string; text: string; chip: string }> = {
  orange: { ring: 'border-brand-orange/25', text: 'text-brand-orange', chip: 'bg-brand-orange/[0.04]' },
  rose: { ring: 'border-brand-rose/25', text: 'text-brand-rose', chip: 'bg-brand-rose/[0.04]' },
  purple: { ring: 'border-brand-purple/25', text: 'text-brand-purple', chip: 'bg-brand-purple/[0.04]' },
};

function RoleCard({
  roleKey,
  icon: Icon,
  accent,
  t,
}: {
  roleKey: 'viewer' | 'publisher' | 'admin';
  icon: typeof Eye;
  accent: 'orange' | 'rose' | 'purple';
  t: (key: string, opts?: Record<string, unknown>) => string;
}) {
  const a = ACCENT[accent];
  // Each role declares a fixed-length capability list in the locale; render
  // returnObjects so translators control how many bullets appear.
  const can = t(`roles.${roleKey}.can`, { returnObjects: true }) as unknown as string[];
  const capabilities = Array.isArray(can) ? can : [];

  return (
    <div
      className={cn(
        'flex flex-col rounded-2xl bg-white border shadow-card p-6 hover:shadow-card-hover hover:-translate-y-0.5 transition-all duration-300',
        a.ring,
      )}
    >
      <div className="flex items-center gap-3 mb-4">
        <div className={cn('p-[2px] rounded-xl bg-brand-gradient shrink-0')}>
          <div className="p-2.5 rounded-xl bg-white">
            <Icon className={cn('w-5 h-5', a.text)} />
          </div>
        </div>
        <div>
          <p className="text-[15px] font-semibold text-ink leading-tight">{t(`roles.${roleKey}.name`)}</p>
          <p className="text-[11px] font-medium uppercase tracking-[0.12em] text-slate-400">
            {t(`roles.${roleKey}.tagline`)}
          </p>
        </div>
      </div>

      <p className="text-xs font-light text-slate-500 leading-relaxed mb-4">{t(`roles.${roleKey}.summary`)}</p>

      <ul className="space-y-2 mb-4">
        {capabilities.map((cap, i) => (
          <li key={i} className="flex items-start gap-2">
            <span
              className={cn(
                'mt-0.5 shrink-0 inline-flex items-center justify-center w-4 h-4 rounded-full',
                a.chip,
              )}
            >
              <ShieldCheck className={cn('w-3 h-3', a.text)} />
            </span>
            <span className="text-[12px] text-slate-600 leading-snug">{cap}</span>
          </li>
        ))}
      </ul>

      <div className="mt-auto pt-3 border-t border-slate-100">
        <p className="text-[10px] font-bold uppercase tracking-[0.14em] text-slate-400 mb-1">
          {t('roles.scopeLabel')}
        </p>
        <p className="text-[12px] font-medium text-slate-600 flex items-center gap-1.5">
          <Users className="w-3.5 h-3.5 text-slate-400 shrink-0" />
          {t(`roles.${roleKey}.scope`)}
        </p>
      </div>
    </div>
  );
}

// ── Minimized guide for regular viewers (readers) ──────────────
function GuideBasic() {
  const { t } = useTranslation('guide');
  const cards = [
    { icon: Inbox, key: 'documents' },
    { icon: Eye, key: 'reading' },
    { icon: Users, key: 'access' },
    { icon: ShieldCheck, key: 'security' },
  ] as const;
  return (
    <AppShell>
      <section className="mb-12 animate-fade-up">
        <p className="text-xs font-bold uppercase tracking-[0.22em] text-brand-purple/70 mb-3 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5" />
          {t('basic.eyebrow')}
        </p>
        <h1 className="text-4xl sm:text-5xl font-medium tracking-tight gradient-text pb-2 max-w-3xl">{t('basic.title')}</h1>
        <p className="text-base font-light text-slate-500 mt-2 max-w-2xl leading-relaxed">{t('basic.intro')}</p>
      </section>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-3xl">
        {cards.map((c, i) => (
          <div
            key={c.key}
            className="rounded-2xl bg-white border border-slate-200/80 shadow-card p-6 animate-fade-up"
            style={{ animationDelay: `${0.06 * (i + 1)}s` }}
          >
            <div className="inline-flex p-[2px] rounded-xl bg-brand-gradient mb-4">
              <div className="p-2.5 rounded-xl bg-white">
                <c.icon className="w-5 h-5 text-brand-purple" />
              </div>
            </div>
            <h3 className="text-base font-semibold text-ink mb-1.5">{t(`basic.cards.${c.key}.title`)}</h3>
            <p className="text-sm font-light text-slate-500 leading-relaxed">{t(`basic.cards.${c.key}.body`)}</p>
          </div>
        ))}
      </div>
    </AppShell>
  );
}
